(() => {
  if (customElements.get('orion-atlas-teaser')) return;
  const SMALL = matchMedia('(max-width: 749px)');
  // Every browser on an iPhone or iPad is Safari's engine, which loads a film only once it plays (preload is ignored).
  const IOS = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  // Autoplay starts once this share of the band is visible, and pauses when it is (almost) gone.
  const PLAY_RATIO = .35;
  const PAUSE_RATIO = .05;
  // From this share on, the band counts as seen (data-seen, for a page that brings its words in then).
  const SEEN_RATIO = .2;
  // The full screen grows out of the banner and goes back into it in this time (the gallery viewer's own pace).
  const THEATER_MS = 700;

  class OrionAtlasTeaser extends HTMLElement {
    // data-held: a carousel holds the film while its slide is away (paused where it is), and frees it to go on from there.
    static get observedAttributes() { return ['data-held']; }

    attributeChangedCallback() {
      if (this._connected) this.update();
    }

    connectedCallback() {
      if (this._connected) return;
      this._video = this.querySelector('video');
      this._toggle = this.querySelector('[data-teaser-toggle]');
      this._sound = this.querySelector('[data-teaser-sound]');
      // Optional (the homepage film): a button that plays the film from its start with sound, and a line that
      // follows its time.
      this._replay = this.querySelector('[data-teaser-replay]');
      this._progress = this.querySelector('[data-teaser-progress]');
      // With data-theater the button opens the film in full screen: its picture and its controls move there.
      this._stage = this.querySelector('[data-teaser-stage]');
      this._controls = this.querySelector('[data-teaser-controls]');
      if (!this._video || !this._toggle || !this._sound) return;
      this._connected = true;
      this._abort = new AbortController();
      const options = { signal: this._abort.signal };
      const video = this._video;
      // Custom controls replace the native ones kept for visitors without JavaScript.
      video.removeAttribute('controls');
      video.muted = true;
      this._ratio = 0;
      this._near = false;
      this._source = null;
      this._userPaused = false;
      this._userStarted = false;
      this._autoplayBlocked = false;
      this._failed = false;
      // The band sits just below the fold on many desktops: never preload while the page itself loads, and let the page
      // settle first (owner, 2026-10-01: a fast homepage), so the film never takes the line from the page's own files.
      this._pageLoaded = document.readyState === 'complete';
      // data-after-load (the homepage's first banner): even in view, the film starts by itself only once the page has
      // settled; the poster holds until then. A visitor's own play is never held.
      this._afterLoad = this.hasAttribute('data-after-load');
      // data-opening (the homepage's first banner; owner, 2026-10-02: the page opens on the film itself, with no picture
      // before it): its file is asked for at once, not after the page; it plays by itself only once the rest of it can
      // follow without a stop (smoothReady), so a faster line starts it sooner and a slow one waits for a longer lead;
      // and it comes up out of the dark only once its pictures move.
      this._opening = this.hasAttribute('data-opening');
      this._smooth = false;
      if (!this._pageLoaded) {
        const settled = () => { this._pageLoaded = true; this.update(); };
        window.addEventListener('load', () => {
          if ('requestIdleCallback' in window) requestIdleCallback(settled, { timeout: 2500 });
          else setTimeout(settled, 1200);
        }, { once: true, signal: this._abort.signal });
      }
      this.updateMode();

      // data-loop-from (seconds): the film loops from there, so an intro (the homepage film's logo, which would sit
      // under the words) plays only when the visitor asks for the whole film.
      this._loopFrom = Math.max(0, parseFloat(this.dataset.loopFrom) || 0);
      this._whole = false;
      if (this._loopFrom) {
        video.loop = false;
        video.removeAttribute('loop');
        video.addEventListener('loadedmetadata', () => {
          if (!this._whole && video.currentTime < this._loopFrom) video.currentTime = this._loopFrom;
        }, options);
        video.addEventListener('ended', () => {
          this._whole = false;
          video.currentTime = this._loopFrom;
          if (!this._userPaused) this.play();
        }, options);
      }

      this._toggle.addEventListener('click', () => this.onToggle(), options);
      this._sound.addEventListener('click', () => this.onSound(), options);
      this._replay?.addEventListener('click', () => this.replay(), options);
      video.addEventListener('playing', () => {
        const start = () => {
          clearTimeout(this._startTimer);
          if (!this._connected || this.hasAttribute('data-started')) return;
          this.setAttribute('data-started', '');
          this.render();
          this.follow();
        };
        // The opening film comes up with its first frame on screen, not on the promise of one.
        if (this._opening && !this.hasAttribute('data-started') && 'requestVideoFrameCallback' in video) {
          video.requestVideoFrameCallback(start);
          this._startTimer = setTimeout(start, 400);
        } else {
          start();
        }
        this.render();
        this.follow();
      }, options);
      for (const type of ['play', 'pause', 'volumechange']) video.addEventListener(type, () => this.render(), options);
      // The progress line is set again whenever the film stops, stalls, jumps (a loop is a jump too) or changes pace.
      video.addEventListener('waiting', () => { this._stalled = true; this.follow(); }, options);
      video.addEventListener('playing', () => { this._stalled = false; }, { ...options, capture: true });
      for (const type of ['pause', 'seeked', 'ratechange', 'ended', 'emptied']) video.addEventListener(type, () => this.follow(), options);
      video.addEventListener('error', () => this.fail(), options);
      document.addEventListener('visibilitychange', () => this.update(), options);
      // A browser that refused to start the film by itself (Safari on an iPhone in low power mode, or with video
      // previews turned off) lets it start at the visitor's first touch, click or key anywhere on the page: that
      // gesture frees it, and it goes on by itself from then on.
      const unlock = () => {
        if (!this._autoplayBlocked || this._userPaused || this._failed) return;
        const attempt = video.play();
        if (!attempt) return;
        attempt.then(() => {
          this._autoplayBlocked = false;
          this.updateMode();
          if (document.hidden || this.hasAttribute('data-held') || this._ratio < PAUSE_RATIO) video.pause();
          this.render();
        }).catch(() => {});
      };
      for (const type of ['touchend', 'click', 'keydown']) document.addEventListener(type, unlock, { capture: true, passive: true, signal: this._abort.signal });
      window.addEventListener('pageshow', () => this.update(), options);
      SMALL.addEventListener('change', () => { if (this._source) this.load(); }, options);

      // The opening film is the first thing on the page: on a fast line (and on an iPhone or iPad, which starts it at
      // once anyway) its file is asked for now, not after the first layout lets the observers say it is in view; they
      // correct this as soon as they report.
      if (this._opening && (IOS || this.fastLine())) {
        this._near = true;
        this._ratio = 1;
        this.update();
      }

      if ('IntersectionObserver' in window) {
        this._nearObserver = new IntersectionObserver(entries => {
          this._near = entries[entries.length - 1].isIntersecting;
          this.update();
        }, { rootMargin: '50% 0px' });
        this._viewObserver = new IntersectionObserver(entries => {
          const entry = entries[entries.length - 1];
          this._ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
          if (this._ratio >= SEEN_RATIO) this.setAttribute('data-seen', '');
          this.update();
        }, { threshold: [0, PAUSE_RATIO, SEEN_RATIO, PLAY_RATIO, .6, 1] });
        this._nearObserver.observe(this);
        this._viewObserver.observe(this);
      } else {
        this.setAttribute('data-seen', '');
      }
      this.render();
    }

    disconnectedCallback() {
      if (!this._connected) return;
      this._connected = false;
      if (this._theater) this.closeTheater(true);
      this._abort.abort();
      this._nearObserver?.disconnect();
      this._viewObserver?.disconnect();
      this._video.pause();
    }

    // Autoplay unless data is being saved or the connection cannot carry even the light film as it plays (2G or 3G, or
    // under about 1.5 Mbps: owner, 2026-10-01, a slow connection never gets stuck on the film); then the poster and a
    // play button. The opening film waits for its lead instead (lead), so only saving data or 2G give it the button.
    // By the owner's rule it plays also with reduced motion.
    updateMode() {
      const connection = navigator.connection || {};
      const saveData = connection.saveData === true;
      const crawling = this._opening
        ? /(^|-)2g$/.test(connection.effectiveType || '')
        : /(^|-)(2g|3g)$/.test(connection.effectiveType || '') || (connection.downlink > 0 && connection.downlink < 1.5);
      this._auto = !saveData && !crawling && !this._autoplayBlocked && 'IntersectionObserver' in window;
      this.dataset.mode = this._auto ? 'auto' : 'manual';
      // The opening film has no picture before it; when it cannot play by itself, its own frame waits under the play
      // button (data-poster-*, loaded only then), so the stage never looks stuck.
      if (!this._auto && this._opening && this._video && !this._video.poster) {
        const poster = (SMALL.matches ? this._video.dataset.posterTall : this._video.dataset.posterWide) || this._video.dataset.posterWide;
        if (poster) this._video.poster = poster;
      }
    }

    // The page's files of the film for this screen (data-sources-*, lightest first, each with its width w and, where
    // known, its bitrate k in kbit/s), or none.
    files() {
      const video = this._video;
      try { return JSON.parse((SMALL.matches ? video.dataset.sourcesTall : video.dataset.sourcesWide) || '[]'); } catch (error) { return []; }
    }

    // The film's file for this screen and connection, from the page's list: the smallest file at least as wide as
    // needed (a phone's vertical film about 1000 px wide, 700 on a small phone; a computer's 1920 px), the lightest on
    // a slow or data-saving connection, else the largest there is. The opening film also keeps to files the line
    // carries with room to spare: the browser's own estimate of the line (downlink), and what its own download has
    // shown (_capKbps, lighten). Without the list (an older page), the file the page chose.
    wanted() {
      const video = this._video;
      const tall = SMALL.matches;
      let list = this.files();
      if (list.length) {
        const connection = navigator.connection || {};
        const slow = connection.saveData === true || /2g|3g/.test(connection.effectiveType || '') || (connection.downlink > 0 && connection.downlink < 3);
        const need = slow ? 0 : tall ? (innerWidth * Math.min(devicePixelRatio || 1, 3) > 800 ? 1000 : 700) : 1900;
        if (this._opening) {
          const cap = Math.min(connection.downlink > 0 ? connection.downlink * 1000 / 1.6 : Infinity, this._capKbps || Infinity);
          const carried = list.filter(file => !(file.k > cap));
          list = carried.length ? carried : list.slice(0, 1);
        }
        return (list.find(file => file.w >= need) || list[list.length - 1]).u;
      }
      return (tall ? video.dataset.srcTall : video.dataset.srcWide) || video.dataset.srcWide || video.dataset.srcTall;
    }

    // preload="none" holds until here: the file is requested only when the band is near or asked for.
    load() {
      const source = this.wanted();
      if (!source || source === this._source) return;
      const video = this._video;
      const resume = this._source !== null && !video.paused;
      this._source = source;
      // A new file: its lead is measured afresh (lead).
      this._samples = [];
      this._loadedAt = performance.now();
      // The file first, then the preload: the other way round, the browser starts on the first <source> it can play
      // and drops it the moment the file is set.
      video.src = source;
      video.preload = 'auto';
      if (resume) this.play();
    }

    play() {
      this.load();
      const attempt = this._video.play();
      attempt?.catch(error => {
        // Autoplay refused (for example in a battery saver mode): fall back to the play button.
        if (error?.name === 'NotAllowedError') {
          this._autoplayBlocked = true;
          this.updateMode();
        }
        this.render();
      });
    }

    update() {
      if (!this._connected || this._failed) return;
      const video = this._video;
      // data-quiet (the homepage opening on its picture): nothing of the film loads while its slide is away.
      const quiet = this.hasAttribute('data-quiet') && this.hasAttribute('data-held');
      if ((!quiet && this._auto && this._near && (this._pageLoaded || this._opening)) || this._userStarted) this.load();
      if (document.hidden || this._userPaused || this.hasAttribute('data-held') || this._ratio < PAUSE_RATIO) {
        if (!video.paused) video.pause();
        return;
      }
      const autoReady = this._auto && (this._pageLoaded || !this._afterLoad);
      if (video.paused && this._ratio >= PLAY_RATIO && (autoReady || this._userStarted)) {
        // The opening film starts by itself only with its lead in hand; a visitor's own play is never held. On an
        // iPhone or iPad there is no lead to wait for (nothing loads before it plays): it plays at once and comes up
        // with its first frame. Whatever happens, after eight seconds in view without a frame, its own frame and the
        // play button are there (holdFrame), never a dark stage.
        if (this._opening && !this.hasAttribute('data-started')) this.holdFrame();
        if (this._opening && !this._smooth && !this._userStarted && !IOS) {
          this.awaitLead();
          return;
        }
        this.play();
      }
    }

    // The opening film's lead: how much of it is loaded ahead of where it stands, and how fast it loads, in seconds of
    // film per second over the last moment. It may start once that lead outlasts the rest of the film at that pace,
    // with room for the line to slow down: at once with a short lead on a fast line, after a longer one on a slow line,
    // so that once it moves it does not stop.
    lead() {
      const video = this._video;
      const now = performance.now();
      const at = video.currentTime;
      let loaded = 0;
      let end = at;
      for (let i = 0; i < video.buffered.length; i++) {
        loaded = Math.max(loaded, video.buffered.end(i));
        if (video.buffered.start(i) <= at + .25) end = Math.max(end, video.buffered.end(i));
      }
      const samples = this._samples || (this._samples = []);
      samples.push({ now, loaded });
      while (samples.length > 2 && now - samples[1].now >= 1200) samples.shift();
      const span = (now - samples[0].now) / 1000;
      const pace = span >= .6 ? Math.max(0, loaded - samples[0].loaded) / span : null;
      const total = video.duration;
      if (video.readyState < 3 || !(total > 0)) return { ready: false, pace };
      const ahead = end - at;
      if (end >= total - .3) return { ready: true, pace };
      // A line the browser itself reports as fast, at least twice the file's own rate, needs no measuring first: the
      // film starts with a short lead at once, so it comes up while the rest is still loading. Chrome, Edge and
      // Android report it; Safari does not, and measures as before.
      if (pace === null && this.fastLine()) return { ready: ahead >= 1.5, pace };
      if (pace === null) return { ready: ahead >= 8, pace };
      // A browser may stop loading a paused film it already holds enough of; then its own word (HAVE_ENOUGH_DATA) counts.
      if (pace < .05 && video.readyState === 4 && ahead >= 3) return { ready: true, pace };
      const safe = pace * .8;
      const left = total - at;
      const need = safe >= 1 ? 1.5 : Math.min(left, (1 - safe) * left + 1.5);
      return { ready: ahead >= need, pace };
    }

    // The browser's own estimate of the line (Chrome, Edge, Android): fast enough when, on 4G or better and without
    // data saving, half of it still carries the file in hand.
    fastLine() {
      const line = navigator.connection;
      if (!line || line.saveData || !(line.downlink > 0) || (line.effectiveType && line.effectiveType !== '4g')) return false;
      const source = this._source || this.wanted();
      const file = this.files().find(item => item.u === source);
      return line.downlink * 1000 * .5 >= (file && file.k ? file.k : 4000);
    }

    // Checks the lead every tenth of a second or so until the film may start, then lets it (update). A line slower than
    // the file in hand, measured once the page's own files are in, moves it to a lighter file (lighten). A browser that
    // loads a film only once it plays (Safari on an iPhone ignores preload; a phone on a data line may too) stops
    // loading by itself (networkState idle) with no lead in hand: then the film plays at once and fills as it goes,
    // rather than wait for ever. Nor does any film wait more than ten seconds.
    awaitLead() {
      if (this._leadTimer) return;
      const check = () => {
        this._leadTimer = null;
        const video = this._video;
        if (!this._connected || this._failed || this._smooth || this._userStarted || !video.paused) return;
        const { ready, pace } = this.lead();
        const since = performance.now() - (this._loadedAt || 0);
        const idle = video.networkState === HTMLMediaElement.NETWORK_IDLE && since > 800;
        // Nothing in after a second and a half: a browser that waits for play, or a line too slow to wait on.
        const starved = since > 1500 && video.readyState < 2;
        if (ready || idle || starved || since > 10000) {
          this._smooth = true;
          this.update();
          return;
        }
        if (pace !== null && pace < 1.25 && since > 1500 && (document.readyState === 'complete' || since > 4000)) this.lighten(pace);
        this._leadTimer = setTimeout(check, 120);
      };
      this._leadTimer = setTimeout(check, 0);
    }

    // The opening film's last resort: eight seconds in view and still no frame on screen (a browser that never
    // started it), its own frame waits under the play button, as in the manual mode; if it starts later, it simply
    // comes up over it.
    holdFrame() {
      if (this._holdTimer) return;
      this._holdTimer = setTimeout(() => {
        if (!this._connected || this.hasAttribute('data-started')) return;
        const video = this._video;
        if (!video.poster) {
          const poster = (SMALL.matches ? video.dataset.posterTall : video.dataset.posterWide) || video.dataset.posterWide;
          if (poster) video.poster = poster;
        }
        this.setAttribute('data-waiting', '');
      }, 8000);
    }

    // The best file the measured line carries with room to spare, if lighter than the one in hand.
    lighten(pace) {
      const file = this.files().find(item => item.u === this._source);
      if (!file || !file.k) return;
      const cap = file.k * pace / 1.6;
      if (this._capKbps && this._capKbps <= cap) return;
      this._capKbps = cap;
      this.load();
    }

    onToggle() {
      if (this._video.paused) {
        this._userPaused = false;
        this._userStarted = true;
        this.play();
      } else {
        this._userPaused = true;
        this._video.pause();
      }
      this.render();
    }

    // Sound is only ever switched on by the visitor.
    onSound() {
      this._video.muted = !this._video.muted;
      this.render();
    }

    // "Watch the teaser": the whole film from its start, with sound (the visitor asked for it), in full screen where
    // the page allows it (data-theater).
    replay() {
      const video = this._video;
      this._userPaused = false;
      this._userStarted = true;
      this._whole = true;
      if (this.hasAttribute('data-theater')) this.openTheater();
      this.load();
      video.muted = false;
      try { video.currentTime = 0; } catch (_) { /* Not seekable yet: it starts from the beginning anyway. */ }
      this.play();
      this.render();
    }

    // Full screen: a modal dialog (the top layer, above the header) takes the film's picture, controls and progress
    // line; the picture grows from the banner's box to the whole screen. The X, Escape, or a scroll or swipe brings
    // it back into the banner, where the film goes on from the same moment with its sound as it is.
    openTheater() {
      if (this._theater || !this._stage) return;
      const dialog = document.createElement('dialog');
      dialog.className = 'orion-home orion-home-film orion-film-theater';
      if (this.dataset.theaterLabel) dialog.setAttribute('aria-label', this.dataset.theaterLabel);
      const veil = document.createElement('div');
      veil.className = 'orion-film-theater__veil';
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'orion-film-theater__close';
      close.setAttribute('aria-label', this.dataset.closeLabel || 'Close');
      close.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m6.5 6.5 11 11m0-11-11 11"/></svg>';
      // Each part leaves a mark where it stood, to go back there.
      this._homes = [this._stage, this._controls, this._progress].filter(Boolean).map(node => {
        const mark = document.createComment('orion-film-theater');
        node.before(mark);
        return { node, mark };
      });
      dialog.append(veil, ...this._homes.map(home => home.node), close);
      this.frame(dialog, this.getBoundingClientRect());
      document.body.appendChild(dialog);
      dialog.showModal();
      this._theater = dialog;
      this.render();
      // A moved element starts its animations afresh: the progress line is set again where the film stands.
      this.follow();
      void dialog.offsetWidth;
      dialog.classList.add('is-open');
      this.frame(dialog, null);
      close.focus({ preventScroll: true });

      this._theaterAbort = new AbortController();
      const signal = this._theaterAbort.signal;
      // A scroll or a swipe closes it and goes on as a scroll: the page moves at once, so the film goes straight back
      // into its banner instead of flying over the moving page.
      const scrolled = () => this.closeTheater('scroll');
      close.addEventListener('click', () => this.closeTheater(), { signal });
      dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeTheater(); }, { signal });
      dialog.addEventListener('wheel', event => { if (Math.abs(event.deltaY) > 2) scrolled(); }, { passive: true, signal });
      let startY = null;
      dialog.addEventListener('touchstart', event => { startY = event.touches[0] ? event.touches[0].clientY : null; }, { passive: true, signal });
      dialog.addEventListener('touchmove', event => {
        if (startY === null || !event.touches[0]) return;
        if (Math.abs(event.touches[0].clientY - startY) > 24) { startY = null; scrolled(); }
      }, { passive: true, signal });
      dialog.addEventListener('keydown', event => {
        const scrolling = ['PageDown', 'PageUp', 'ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) || (event.key === ' ' && !event.target.closest('button'));
        if (scrolling) scrolled();
      }, { signal });
    }

    // The picture's box as insets from the screen's edges: the banner's box, or the whole screen.
    frame(dialog, box) {
      const insets = box ? [box.top, innerWidth - box.right, innerHeight - box.bottom, box.left] : [0, 0, 0, 0];
      ['--t', '--r', '--b', '--l'].forEach((name, index) => dialog.style.setProperty(name, Math.round(insets[index]) + 'px'));
    }

    // mode: undefined (the X or Escape: the picture flies back into the banner while the page holds still), 'scroll'
    // (the picture is back at once and only the dark veil fades, so nothing floats over the moving page) or true
    // (at once, when the element leaves the page).
    closeTheater(mode) {
      const dialog = this._theater;
      if (!dialog || dialog.classList.contains('is-closing')) return;
      this._theaterAbort?.abort();
      dialog.classList.add('is-closing');
      dialog.classList.remove('is-open');
      const home = () => {
        if (!this._homes) return;
        this._homes.forEach(({ node, mark }) => mark.replaceWith(node));
        this._homes = null;
        this.render();
        this.follow();
      };
      const done = () => {
        home();
        if (dialog.open) dialog.close();
        dialog.remove();
        this._theater = null;
        this.render();
        if (mode !== true) this._replay?.focus({ preventScroll: true });
      };
      if (mode === true) { done(); return; }
      if (mode === 'scroll') {
        home();
        setTimeout(done, 340);
        return;
      }
      const hold = event => { if (event.cancelable) event.preventDefault(); };
      dialog.addEventListener('wheel', hold, { passive: false });
      dialog.addEventListener('touchmove', hold, { passive: false });
      this.frame(dialog, this.getBoundingClientRect());
      setTimeout(done, THEATER_MS);
    }

    // The progress line follows the film without a task on every frame: at each start, pause, stall, seek or end it is
    // set where the film stands, and while the film plays a CSS animation (orion-home-stage.liquid) carries it to the
    // end at the film's own pace, on the compositor.
    follow() {
      const bar = this._progress;
      const video = this._video;
      if (!bar || !(video.duration > 0)) return;
      const rate = video.playbackRate || 1;
      const at = Math.min(video.currentTime, video.duration);
      bar.style.setProperty('--progress', (at / video.duration).toFixed(4));
      if (!this._connected || video.paused || video.ended || this._stalled) {
        bar.removeAttribute('data-run');
        return;
      }
      bar.style.setProperty('--progress-length', `${(video.duration / rate).toFixed(3)}s`);
      bar.style.setProperty('--progress-from', `${(-at / rate).toFixed(3)}s`);
      // Two names for the same run, so that a new start restarts it without asking for a layout.
      bar.dataset.run = bar.dataset.run === 'a' ? 'b' : 'a';
    }

    fail() {
      this._failed = true;
      this.dataset.state = 'error';
      this._toggle.hidden = true;
      this._sound.hidden = true;
    }

    render() {
      if (this._failed) return;
      const video = this._video;
      const playing = !video.paused && !video.ended;
      const started = this.hasAttribute('data-started');
      this.dataset.state = playing ? 'playing' : started ? 'paused' : 'idle';
      this.toggleAttribute('data-muted', video.muted);
      this._toggle.setAttribute('aria-label', playing ? this.dataset.pauseLabel : this.dataset.playLabel);
      this._sound.hidden = !started;
      this._sound.setAttribute('aria-label', video.muted ? this.dataset.soundOnLabel : this.dataset.soundOffLabel);
      // In full screen the controls sit in the dialog: it carries the same state for their icons.
      if (this._theater) {
        this._theater.dataset.state = this.dataset.state;
        this._theater.toggleAttribute('data-muted', video.muted);
        this._theater.toggleAttribute('data-started', started);
      }
    }
  }
  customElements.define('orion-atlas-teaser', OrionAtlasTeaser);
})();
